// PLAT-2857 — Confirm accept action switch. The approve modal performs the finalize call and
// hands the result here; this composable routes blocked outcomes, opens the optional Plaid
// upgrade, or advances the flow. The reject path never comes through here.
import { useRouter } from 'vue-router';
import type { SccSubmitResult } from '@Types/SccSubmitResult.interface';
import useFlowNavigation from '@Use/useFlowNavigation';
import useSccPlaidUpgrade from '@Use/useSccPlaidUpgrade';

export default function useConfirmSubmit() {
    const router = useRouter();
    const { advanceFlow } = useFlowNavigation();
    const { plaidLinkToken, isPlaidModalOpen, isCompletingPlaid, openPlaid, onPlaidSuccess, onPlaidExit } =
        useSccPlaidUpgrade(advanceFlow);

    /**
     * Handle the finalize result of an accepted confirm. Returns true when the participant
     * stays on the page with the Plaid modal open (the caller should drop its navigation
     * state); false when the flow navigated away (blocked screen or advance).
     */
    const handleAcceptResult = async (result: SccSubmitResult | null | undefined): Promise<boolean> => {
        // A missing body/action means a pre-PLAT-2857 BFF — the legacy success outcome.
        const action = result?.action ?? 'success';

        if (action === 'blocked') {
            await router.push({
                name: 'request-blocked',
                params: router.currentRoute.value.params,
                query: router.currentRoute.value.query,
            });
            return false;
        }

        if (action === 'open_plaid' && result?.plaidLinkToken) {
            openPlaid(result.plaidLinkToken);
            return true;
        }

        advanceFlow();
        return false;
    };

    return {
        plaidLinkToken,
        isPlaidModalOpen,
        isCompletingPlaid,
        handleAcceptResult,
        onPlaidSuccess,
        onPlaidExit,
    };
}
